import type { PayloadAction } from '@reduxjs/toolkit';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import { classifyInfraError } from '@/utils/infra-error';

import type { AccountDraft } from '../domain/account-draft.model';
import { validateDraft } from '../domain/account-draft.validation';
import { toAccountSettingsError } from '../domain/account-settings-error';
import { avatarFileProblem } from '../domain/avatar-file.rules';
import type { AccountSettingsGateway } from '../infrastructure/account-settings.gateway';
import {
  avatarRemoveRequested,
  avatarUploadRequested,
  submitRequested,
} from './account-settings.actions';
import { selectDraft } from './account-settings.selectors';
import { accountSettingsActions } from './account-settings.slice';

/**
 * WORKER: persist the edited draft. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway. All
 * I/O goes through the injected gateway (read from the saga `context`); the
 * write hits the self endpoint `PATCH /account`, so no user id is threaded here.
 */
function* saveWorker() {
  const draft = (yield select(selectDraft)) as AccountDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(accountSettingsActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: AccountSettingsGateway = yield getContext('accountSettingsGateway');
  yield put(accountSettingsActions.saveStarted());
  try {
    yield call(gateway.updateAccount, draft);
    yield put(accountSettingsActions.saveSucceeded());
  } catch (e) {
    yield put(accountSettingsActions.saveFailed(toAccountSettingsError(e)));
  }
}

/**
 * WORKER: send a picked profile photo (#579). The file is checked first (pure
 * domain), so a wrong type or size is refused without an upload; the server's
 * own refusal (an image it cannot decode) arrives as the API's message.
 */
function* uploadAvatarWorker(action: PayloadAction<File>) {
  const problem = avatarFileProblem(action.payload);
  if (problem) {
    yield put(accountSettingsActions.avatarFailed(problem));
    return;
  }

  const gateway: AccountSettingsGateway = yield getContext('accountSettingsGateway');
  yield put(accountSettingsActions.avatarUploadStarted());
  try {
    yield call(gateway.uploadAvatar, action.payload);
    yield put(accountSettingsActions.avatarUploaded());
  } catch (e) {
    yield put(
      accountSettingsActions.avatarFailed(
        classifyInfraError(e, 'Failed to update the photo.').message,
      ),
    );
  }
}

/** WORKER: remove the profile photo (#579). */
function* removeAvatarWorker() {
  const gateway: AccountSettingsGateway = yield getContext('accountSettingsGateway');
  yield put(accountSettingsActions.avatarRemoveStarted());
  try {
    yield call(gateway.removeAvatar);
    yield put(accountSettingsActions.avatarRemoved());
  } catch (e) {
    yield put(
      accountSettingsActions.avatarFailed(
        classifyInfraError(e, 'Failed to remove the photo.').message,
      ),
    );
  }
}

/** WATCHERS. */
export function* accountSettingsSaga() {
  yield all([
    takeLatest(submitRequested.type, saveWorker),
    takeLatest(avatarUploadRequested.type, uploadAvatarWorker),
    takeLatest(avatarRemoveRequested.type, removeAvatarWorker),
  ]);
}
